import { useEffect, useRef, useCallback } from "react";
import type { ReviewPolygon } from "@/types/document-review";
import * as fabric from "fabric";

type PolygonManagerProps = {
    pageNumber: number;
    pageWidth: number;
    pageHeight: number;
    onChange?: (polygonId: string, updates: Partial<Omit<ReviewPolygon, "id" | "createdAt" | "updatedAt">>) => void;
    polygons?: ReviewPolygon[];
    onSelect?: (polygonId: string | null) => void;
    selectedPolygonId?: string | null;
    onDelete?: (polygonId: string) => void;
};

export function PolygonManager({ pageNumber, pageWidth, pageHeight, onChange, polygons = [], onSelect, selectedPolygonId, onDelete }: PolygonManagerProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const fabricRef = useRef<fabric.Canvas | null>(null);
    const isInteractingRef = useRef(false);
    const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Funzione per determinare il colore del bordo in base allo stato del poligono
    const getPolygonBorderColor = (polygon: ReviewPolygon): string => {
        const isResolved = polygon.attributes?.resolved === 'true';
        return isResolved ? '#10B981' : '#EF4444'; // success (verde) o error (rosso)
    };

    const emitChange = useCallback((polygonId: string, updates: Partial<Omit<ReviewPolygon, "id" | "createdAt" | "updatedAt">>) => {
        // Call onChange immediately, no debounce - the canvas is the source of truth
        onChange?.(polygonId, updates);
    }, [onChange]);

    useEffect(() => {
        if (!canvasRef.current) return;
        // Wait for valid dimensions before initializing canvas
        if (pageWidth <= 0 || pageHeight <= 0) return;
        
        // initialize Fabric canvas once
        let canvas = fabricRef.current;
        if (!canvas) {
            canvas = new (fabric as any).Canvas(canvasRef.current, { selection: true, skipTargetFind: false, hoverCursor: 'move' });
            fabricRef.current = canvas;
        }

        if (!canvas) return;
        canvas.setWidth(pageWidth);
        canvas.setHeight(pageHeight);
        (canvas as any).backgroundColor = "rgba(0,0,0,0)";
        canvas.renderAll();

        const emitChangeFromObject = (obj: fabric.Rect | undefined) => {
            if (!obj) return;
            const id = (obj as any).data?.id as string | undefined;
            if (!id) return; // Only emit changes for objects with IDs
            
            const scaledW = (obj as any).getScaledWidth ? (obj as any).getScaledWidth() : (obj.width ?? 0);
            const scaledH = (obj as any).getScaledHeight ? (obj as any).getScaledHeight() : (obj.height ?? 0);
            
            emitChange(id, {
                x: (obj.left ?? 0) / pageWidth,
                y: (obj.top ?? 0) / pageHeight,
                width: scaledW / pageWidth,
                height: scaledH / pageHeight,
            });
        };

        const handleObjectModified = (opt: any) => {
            const obj = opt.target as fabric.Rect | undefined;
            emitChangeFromObject(obj);
            
            // Reset interaction flag after a delay to allow state propagation
            // This prevents the sync effect from overwriting changes too early
            if (interactionTimeoutRef.current) {
                clearTimeout(interactionTimeoutRef.current);
            }
            interactionTimeoutRef.current = setTimeout(() => {
                isInteractingRef.current = false;
            }, 150); // 150ms delay to ensure React state has propagated
        };

        const handleSelection = (opt: any) => {
            const obj = opt.selected?.[0] as fabric.Object | undefined;
            const id = (obj as any)?.data?.id as string | undefined;
            onSelect?.(id ?? null);
        };

        const handleMouseDown = () => {
            isInteractingRef.current = true;
            // Clear any pending reset
            if (interactionTimeoutRef.current) {
                clearTimeout(interactionTimeoutRef.current);
            }
        };

        // Register canvas event listeners
        if (!canvas) return;
        (canvas as any).off();
        canvas.on("object:modified", handleObjectModified as any);
        canvas.on("selection:created", handleSelection as any);
        canvas.on("selection:updated", handleSelection as any);
        canvas.on("selection:cleared", (() => onSelect?.(null)) as any);
        canvas.on("mouse:down", handleMouseDown as any);

        return () => {
            // do not dispose here on prop changes; only dispose on unmount
        };
    }, [pageHeight, pageNumber, pageWidth, emitChange, onSelect]);

    // Keyboard shortcuts (Delete key)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Delete' || e.key === 'Backspace') {
                const canvas = fabricRef.current;
                if (!canvas) return;

                const activeObj = canvas.getActiveObject();
                if (activeObj && (activeObj as any).data?.id) {
                    const id = (activeObj as any).data.id;
                    canvas.remove(activeObj);
                    canvas.renderAll();
                    onSelect?.(null);
                    onDelete?.(id);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onSelect, onDelete]);

    // Sync external polygons to Fabric objects
    useEffect(() => {
        const canvas = fabricRef.current;
        if (!canvas) return;
        // Wait for valid dimensions and ensure canvas is initialized
        if (pageWidth <= 0 || pageHeight <= 0) return;
        if (!canvas.getElement()?.offsetParent) return; // Wait for canvas to be visible

        // Only render polygons for this specific page
        const pagePolygons = polygons.filter((p) => p.pageNumber === pageNumber);
        
        // Get all existing objects on canvas
        const allObjects = canvas.getObjects() as fabric.Rect[];
        const existingObjects = allObjects.filter((o: any) => o.data?.id) as fabric.Rect[];
        const existingIds = new Set(existingObjects.map((o: any) => o.data?.id));
        
        // Find polygons that need to be added (not already on canvas)
        const polygonsToAdd = pagePolygons.filter((p) => !existingIds.has(p.id));
        
        // Find objects that need to be removed (not in polygons array)
        const polygonIds = new Set(pagePolygons.map((p) => p.id));
        const objectsToRemove = existingObjects.filter((o: any) => {
            // Remove if it has an ID but is not in polygons array
            return o.data?.id && !polygonIds.has(o.data.id);
        });
        
        // ALWAYS remove objects that are no longer in the polygons array
        objectsToRemove.forEach((obj) => {
            canvas.remove(obj);
        });
        
        // Update dimensions if they changed
        if (canvas.getWidth() !== pageWidth || canvas.getHeight() !== pageHeight) {
            canvas.setWidth(pageWidth);
            canvas.setHeight(pageHeight);
        }
        
        // ALWAYS add new polygons (those that don't exist on canvas yet)
        // This ensures newly created polygons appear immediately
        polygonsToAdd.forEach((p) => {
            const borderColor = getPolygonBorderColor(p);
            const rect = new fabric.Rect({
                left: p.x * pageWidth,
                top: p.y * pageHeight,
                width: p.width * pageWidth,
                height: p.height * pageHeight,
                fill: "rgba(0,0,0,0)",
                stroke: borderColor,
                strokeWidth: 2,
                selectable: true,
                evented: true,
                hasControls: true,
                transparentCorners: false,
                cornerColor: borderColor,
                cornerSize: 8,              // Ridotto da default (13)
                cornerStyle: 'circle',      // Cerchi invece di quadrati
                borderColor: borderColor,
                borderScaleFactor: 1,
            });
            (rect as any).data = { id: p.id };
            canvas.add(rect);
        });
        
        // Update existing polygons positions/sizes and colors ONLY if not currently interacting
        // This ensures persistence on reload while keeping canvas as source of truth during interactions
        if (!isInteractingRef.current) {
            existingObjects.forEach((obj: any) => {
                const polygon = pagePolygons.find((p) => p.id === obj.data?.id);
                if (polygon) {
                    const newLeft = polygon.x * pageWidth;
                    const newTop = polygon.y * pageHeight;
                    const newWidth = polygon.width * pageWidth;
                    const newHeight = polygon.height * pageHeight;
                    const newBorderColor = getPolygonBorderColor(polygon);
                    const currentStroke = (obj as any).stroke as string | undefined;
                    
                    // Update color if it changed
                    if (currentStroke !== newBorderColor) {
                        obj.set({
                            stroke: newBorderColor,
                            cornerColor: newBorderColor,
                            borderColor: newBorderColor,
                        });
                        obj.setCoords();
                    }
                    
                    // Only update if position/size changed significantly
                    const threshold = 1; // 1 pixel threshold
                    if (
                        Math.abs((obj.left ?? 0) - newLeft) > threshold ||
                        Math.abs((obj.top ?? 0) - newTop) > threshold ||
                        Math.abs((obj.width ?? 0) - newWidth) > threshold ||
                        Math.abs((obj.height ?? 0) - newHeight) > threshold
                    ) {
                        obj.set({
                            left: newLeft,
                            top: newTop,
                            width: newWidth,
                            height: newHeight,
                        });
                        obj.setCoords(); // Important: update control coordinates
                    }
                }
            });
        }
        
        canvas.renderAll();

        // ALWAYS keep selection in sync with React state
        // This ensures clicking on sidebar items selects the polygon on canvas
        if (selectedPolygonId) {
            const found = canvas.getObjects().find((o: any) => o.data?.id === selectedPolygonId);
            if (found) {
                canvas.setActiveObject(found as any);
                canvas.requestRenderAll();
            }
        }
    }, [polygons, selectedPolygonId, pageHeight, pageNumber, pageWidth]);

    // Dispose on unmount
    useEffect(() => {
        return () => {
            if (interactionTimeoutRef.current) {
                clearTimeout(interactionTimeoutRef.current);
            }
            if (fabricRef.current) {
                fabricRef.current.dispose();
                fabricRef.current = null;
            }
        };
    }, []);

    return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-auto" />;
}


